/**
 * State-level guide content — Ohio
 *
 * SCHEMA CONTRACT (Feb 2026):
 * This file holds all statewide content shared across every Ohio city guide:
 *   - Statute quick-reference grid
 *   - Full Ohio law detail
 *   - Funding options comparison table (Ohio-cited)
 *   - Case types we fund in the state
 *   - Instabridge differentiators (worded for Ohio's regulatory framework)
 *   - State-wide funding FAQs (product-level, not city-specific)
 *   - Sidebar "governed by" strings & trust chips
 *
 * To scale into PA / IN / TX / etc.: create sibling files with the same shape
 * (`/states/pennsylvania.js`, etc.). The CityGuide component reads whichever
 * state file the city's `state_ref` points at.
 *
 * PROVENANCE: Every substantive legal, statutory, and product claim carries an
 * inline `source_ref` field OR is described narrowly per user's guardrails.
 */
export const OHIO_STATE_GUIDE = {
  slug: "ohio",
  name: "Ohio",
  abbreviation: "OH",

  // Trust chips shared on every Ohio city hero
  trust_chips: [
    "Non-recourse (§ 1349.55)",
    "No credit check",
    "24-hour funding after attorney verification",
    "10-business-day cancellation from 10/6/2026",
  ],

  // Fast-facts sidebar — "governed by" strings that render on every Ohio city
  regulatory_labels: {
    governed_by_primary: "R.C. § 1349.55",
    governed_by_incoming: "§ 1357.03",
    cancellation_now: "5 days",
    cancellation_incoming: "10 biz days from 10/6/26",
  },

  // ─────────────────────────────────────────────────────────
  // Statute quick-reference — same 6 statutes matter in every Ohio city
  // ─────────────────────────────────────────────────────────
  statute_table: {
    heading: "Ohio statutes at a glance",
    intro:
      "The six Ohio statutes that most directly affect an Ohio injury case — with what each one does and why it matters for your recovery.",
    rows: [
      {
        code: "R.C. § 2305.10",
        title: "Bodily-injury statute of limitations",
        effect: "2 years from date of injury (discovery rule applies to latent/toxic exposure).",
        why_it_matters: "Miss it and the case dies, regardless of merit.",
      },
      {
        code: "R.C. § 2305.113",
        title: "Medical malpractice",
        effect: "1 year from discovery, extendable +180 days by written notice; 4-year statute of repose.",
        why_it_matters: "Ohio's tightest injury deadline. Delay is fatal.",
      },
      {
        code: "R.C. § 2315.33",
        title: "Comparative negligence",
        effect: "Recover if your fault ≤ 50%; damages reduced by your fault share.",
        why_it_matters: "Single biggest driver of settlement value in contested-liability cases.",
      },
      {
        code: "R.C. § 2315.18",
        title: "Non-economic damages cap",
        effect: "$250K or 3× economic damages, up to $350K/plaintiff or $500K/occurrence. Cap lifted for catastrophic injuries.",
        why_it_matters: "Limits pain-and-suffering. Economic damages (medical + wage loss) are NOT capped.",
      },
      {
        code: "R.C. § 1349.55  →  R.C. § 1357.03",
        title: "Pre-settlement funding (Ohio)",
        effect: "Non-recourse, plain-language disclosures, attorney acknowledgment. § 1349.55 (5-day cancellation) is expressly repealed effective 10/6/2026 and replaced by new Chapter 1357 (10-business-day cancellation) under HB 105.",
        why_it_matters: "This is what makes funding legal — and safe — in Ohio.",
      },
      {
        code: "R.C. Chapter 2744",
        title: "Political-subdivision immunity",
        effect: "Cases against a city, county, or agency have different immunity + notice rules.",
        why_it_matters: "Sidewalks, city vehicles, county property — timing and gates are different.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // Ohio law — full detail
  // ─────────────────────────────────────────────────────────
  ohio_law: {
    heading: "Ohio law every plaintiff should understand",
    intro:
      "Six statutes materially affect an Ohio injury case. Understanding them helps you evaluate settlement offers, avoid deadline traps, and understand what your case is actually worth.",
    items: [
      {
        h3: "Statute of limitations for bodily injury — R.C. § 2305.10",
        body:
          "In Ohio, a claim for bodily injury generally must be filed within two years after the cause of action accrues. For most physical injuries, this means two years from the date of the accident or event. Certain latent-injury and toxic-exposure cases follow a discovery rule — the clock starts when a competent medical authority informs the plaintiff of the injury or when the plaintiff should have known through reasonable diligence. Missing the statute of limitations is fatal to a case, regardless of how strong the underlying facts are.",
        source_ref: "Ohio Rev. Code § 2305.10(A)",
      },
      {
        h3: "Medical malpractice — R.C. § 2305.113",
        body:
          "Under R.C. § 2305.113(A), a medical malpractice claim must be commenced within one year after the cause of action accrues. The plaintiff can extend the one-year period by an additional 180 days by serving a written notice of intent to sue on the potential defendant by certified mail, return receipt requested, before the initial year expires. Section (C) imposes an absolute four-year statute of repose measured from the act or omission. Section (D) provides limited discovery-rule exceptions: (1) if the injury could not have been discovered within three years but is discovered before the four-year repose period expires, the plaintiff may file within one year of discovery; and (2) foreign-object cases (an object left in the body) may be filed within one year of discovery. Med-mal deadlines are the tightest of any Ohio injury case; if you suspect medical malpractice, do not delay in consulting with an attorney.",
        source_ref: "Ohio Rev. Code § 2305.113(A), (B), (C), (D) — verified against codes.ohio.gov (authenticated version effective 10/3/2023, HB 33 – 135th G.A.).",
      },
      {
        h3: "Comparative negligence — R.C. § 2315.33",
        body:
          "Under R.C. § 2315.33, a plaintiff's contributory fault does not bar recovery if that fault is not greater than the combined tortious conduct of all other persons — including persons the plaintiff sues in the action AND persons the plaintiff does not sue. If the plaintiff's fault exceeds that combined figure, the plaintiff recovers nothing. If the plaintiff's fault is 50% or less of the total, damages are recoverable but reduced proportionately by the plaintiff's percentage of fault, as determined under R.C. § 2315.34. Comparative-negligence exposure is one of the biggest single drivers of settlement value in cases where liability is contested (motorcycle-vs-car left-turn cases, disputed lane-change collisions, slip-and-fall cases with visible hazards, etc.).",
        source_ref: "Ohio Rev. Code § 2315.33 (SB 80, 125th G.A., effective 4/7/2005) — verified against codes.ohio.gov; damages-reduction mechanism at § 2315.34.",
      },
      {
        h3: "Non-economic damages cap — R.C. § 2315.18",
        body:
          "Ohio caps non-economic damages (pain and suffering, loss of enjoyment, mental anguish) in most tort actions. Under R.C. § 2315.18(B)(2), the cap is the greater of $250,000 or three times economic loss, up to a ceiling of $350,000 per plaintiff or $500,000 per single occurrence. Economic damages — past and future medical expenses, lost income, out-of-pocket loss — are not capped. Section (B)(3) removes the cap where the plaintiff has sustained (a) permanent and substantial physical deformity, loss of use of a limb, or loss of a bodily organ system; or (b) a permanent physical functional injury that permanently prevents the injured person from being able to independently care for self and perform life-sustaining activities. Ohio courts have generally held that stand-alone emotional and mental injuries do not qualify for the exception, though outcomes turn on specific facts and expert testimony. Under § 2315.18(F)(2), the court does not instruct the jury on the cap and counsel may not inform the jury of it; the cap is applied by the court after verdict.",
        source_ref: "Ohio Rev. Code § 2315.18(B)(2), (B)(3), (F)(2) — verified against codes.ohio.gov (authenticated version effective 4/15/2021, HB 352 – 133rd G.A.). Cap-application procedure discussed in Simpkins v. Grace Brethren Church, 149 Ohio St.3d 307 (2016).",
      },
      {
        h3: "Pre-settlement funding — R.C. § 1349.55 (current) and R.C. § 1357.03 (effective 10/6/2026 under HB 105)",
        body:
          "Ohio's Supreme Court in Rancman v. Interim Settlement Funding Corp. (2003) initially held that non-recourse litigation advances were void as champerty and maintenance, but the Court expressly noted the Legislature could authorize them. The General Assembly did so by enacting R.C. § 1349.55, which requires funding contracts to be non-recourse, include specified plain-language disclosures, provide a five-business-day right of cancellation, and be acknowledged in writing by the consumer's attorney. R.C. § 1349.55 is expressly repealed effective October 6, 2026, and is replaced by a new consumer-protection framework in Ohio Revised Code Chapter 1357 (added by Ohio HB 105, 136th G.A.). Under new R.C. § 1357.03(A)(4), consumers gain an extended right to cancel — ten business days after the funding date — by returning the full amount of disbursed funds via uncashed check delivered in person or mailed with a postmark within ten business days. Section 1357.03(A)(6) adds more specific attorney acknowledgment requirements, and § 1357.03(B) makes violations an unfair or deceptive act under R.C. § 1345.02. Instabridge complies fully with § 1349.55 today and is prepared to comply with Chapter 1357 on the effective date.",
        source_ref:
          "Ohio Rev. Code § 1349.55 (HB 248, 127th G.A., effective 8/27/2008; expressly repealed effective 10/6/2026 per header at codes.ohio.gov) and § 1357.03 (HB 105, 136th G.A., effective 10/6/2026, authenticated PDF at codes.ohio.gov). Rancman v. Interim Settlement Funding Corp., 99 Ohio St.3d 121, 2003-Ohio-2721.",
      },
      {
        h3: "Political-subdivision notice — R.C. Chapter 2744",
        body:
          "If your case is against a political subdivision — a city, county, or agency — Ohio Rev. Code Chapter 2744 governs the claim, and different immunity rules and notice requirements apply. These cases have more procedural gates than standard private-party PI cases. If your case involves a governmental defendant, your attorney will need to comply with the specific requirements of Chapter 2744 — do not delay retaining counsel.",
        source_ref: "Ohio Rev. Code Chapter 2744",
      },
    ],
    inline_cta: { to: "/services/pre-settlement", label: "Learn how Instabridge structures Ohio contracts" },
  },

  // ─────────────────────────────────────────────────────────
  // Comparison table — Ohio-statute-cited
  // ─────────────────────────────────────────────────────────
  comparison: {
    heading: "Instabridge vs. other ways to bridge the wait",
    intro:
      "You have options when you need cash before your case settles. Here is an honest comparison of how pre-settlement funding from Instabridge compares to the alternatives Ohio plaintiffs commonly weigh.",
    columns: [
      { key: "instabridge", label: "Instabridge", highlight: true },
      { key: "personal_loan", label: "Personal loan" },
      { key: "credit_card", label: "Credit card" },
      { key: "typical_funder", label: "Typical funder" },
    ],
    rows: [
      {
        criterion: "You owe nothing if you lose",
        instabridge: { v: "Yes", note: "Non-recourse under R.C. § 1349.55" },
        personal_loan: { v: "No", note: "Repayment required regardless" },
        credit_card: { v: "No", note: "Balance survives case outcome" },
        typical_funder: { v: "Yes", note: "Generally non-recourse in Ohio" },
      },
      {
        criterion: "Credit check required",
        instabridge: { v: "No", note: "Not pulled at any point" },
        personal_loan: { v: "Yes", note: "Hard credit inquiry" },
        credit_card: { v: "Yes", note: "Hard credit inquiry" },
        typical_funder: { v: "No", note: "Standard for the industry" },
      },
      {
        criterion: "Monthly payments while case pending",
        instabridge: { v: "None", note: "Paid at settlement, from settlement" },
        personal_loan: { v: "Required", note: "Missed payments hit credit" },
        credit_card: { v: "Required", note: "Minimums accrue interest" },
        typical_funder: { v: "None", note: "Paid at settlement" },
      },
      {
        criterion: "Advance cap discipline",
        instabridge: { v: "8–15% of conservative case value", note: "Settlement-protective" },
        personal_loan: { v: "Based on your credit", note: "Not tied to case value" },
        credit_card: { v: "Your credit limit", note: "Not tied to case value" },
        typical_funder: { v: "Often 20–40% of projected value", note: "Overfunding risk" },
      },
      {
        criterion: "Full multi-year cost schedule in writing",
        instabridge: { v: "Yes — every 6-month tier to 4+ yrs", note: "In every contract" },
        personal_loan: { v: "APR + payment schedule", note: "Standard disclosure" },
        credit_card: { v: "APR only", note: "Assumes revolving use" },
        typical_funder: { v: "Varies", note: "Often only 6- or 12-month math" },
      },
      {
        criterion: "Right to cancel after signing",
        instabridge: {
          v: "5 days now / 10 business days from 10/6/2026",
          note: "R.C. § 1349.55 → R.C. § 1357.03 (HB 105)",
        },
        personal_loan: { v: "Varies by lender", note: "Not statutorily required" },
        credit_card: { v: "N/A", note: "Not a fixed advance" },
        typical_funder: {
          v: "5 days now / 10 business days from 10/6/2026",
          note: "Same Ohio statutory floor",
        },
      },
      {
        criterion: "Impact on credit report",
        instabridge: { v: "None", note: "Never reported" },
        personal_loan: { v: "Balance + payment history reported", note: "Missed payments hurt" },
        credit_card: { v: "Balance + payment history reported", note: "Utilization ratio impact" },
        typical_funder: { v: "None", note: "Standard for the industry" },
      },
      {
        criterion: "Attorney acknowledgment required",
        instabridge: { v: "Yes", note: "Ohio law requires it" },
        personal_loan: { v: "No", note: "Personal debt" },
        credit_card: { v: "No", note: "Personal debt" },
        typical_funder: { v: "Yes", note: "Ohio law requires it" },
      },
    ],
    footnote:
      "Comparison is intended for orientation, not legal advice. Individual products from any category vary; specific terms should be evaluated against your own case, timeline, and financial picture with your attorney.",
    inline_cta: { to: "/tools/funding-cost-calculator", label: "Estimate your Instabridge cost" },
  },

  // ─────────────────────────────────────────────────────────
  // Case types — same 6 across the state
  // ─────────────────────────────────────────────────────────
  case_types_base: {
    heading_template: "The {city} cases we fund",
    intro:
      "Instabridge funds the same range of injury cases across Ohio. Below is a brief description of each with links to our dedicated guides. Every case is evaluated on its individual facts.",
    types: [
      {
        slug: "car-accidents",
        label: "Car accidents",
        body:
          "Rear-end, left-turn, lane-change, and intersection collisions. Governed by the same two-year statute (§ 2305.10) as any bodily-injury claim.",
      },
      {
        slug: "truck-accidents",
        label: "Commercial truck accidents",
        body:
          "Cases involving semis, delivery trucks, and other commercial vehicles. These cases carry federal Motor Carrier Safety Regulations liability layers and can involve higher coverage stacks — and longer timelines — than passenger-vehicle cases.",
      },
      {
        slug: "motorcycle-accidents",
        label: "Motorcycle accidents",
        body:
          "Rider injury cases are governed by the same two-year statute (§ 2305.10) as any bodily-injury claim. Ohio law does not require adult riders to wear a helmet under § 4511.53, and helmet-use arguments cannot bar recovery — but comparative-negligence disputes are more common in rider cases than in car cases, which matters for underwriting.",
      },
      {
        slug: "slip-and-fall",
        label: "Slip and fall / premises liability",
        body:
          "Grocery-store, retail, apartment, and property-owner cases. Ohio's 'open and obvious' doctrine, the natural-accumulation rule for winter cases, and the political-subdivision notice requirements for city-owned property (§ 2744) all affect these cases.",
      },
      {
        slug: "workplace-injury",
        label: "Workplace injury (third-party) cases",
        body:
          "Ohio is a monopolistic workers' compensation state — BWC is the exclusive employer carrier under § 4123.74. Third-party work-injury cases (against a subcontractor, equipment manufacturer, or property owner) proceed as separate personal-injury lawsuits alongside the BWC claim. BWC's subrogation lien attaches to any third-party recovery; we underwrite around it.",
      },
      {
        slug: "medical-malpractice",
        label: "Medical malpractice",
        body:
          "Med-mal cases against physicians, hospitals, and health systems. These cases run under the tightest statute of limitations (§ 2305.113, one year) and require an affidavit of merit under Civ.R. 10(D)(2). Case timelines routinely reach 24–36 months.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // Differentiators — company-level, worded for Ohio compliance
  // ─────────────────────────────────────────────────────────
  differentiators: {
    heading_template: "What makes Instabridge different for {city} plaintiffs",
    items: [
      {
        h3: "Attorney-founded",
        body:
          "Adam Gurney, a practicing attorney, founded Instabridge after observing how the pre-settlement funding industry actually operated from the plaintiff-attorney side of the table. That perspective drives how we underwrite (case merits, not credit models), how we structure advance amounts (settlement-protective caps, not maximum-fee optimization), and how we write our contracts (plain language and full disclosure, not fine print).",
        cta: { label: "About the founder", to: "/about" },
      },
      {
        h3: "Settlement-protective advance caps",
        body:
          "We cap advances at 8–15% of the conservative projected settlement, not the optimistic one. Overfunding is the biggest ethical problem in this industry: a plaintiff who accepts a 30–40% advance can find themselves at settlement owing more than the case pays out. Our cap is deliberate, and we would rather turn away a case than overfund it.",
        cta: { label: "See how we underwrite", to: "/how-it-works" },
      },
      {
        h3: "Full multi-year cost schedule in every contract",
        body:
          "Ohio Rev. Code § 1349.55 requires certain disclosures. We go further: every Instabridge contract includes a complete cost table showing what you owe if the case settles at 6, 12, 18, 24, 30, 36, and 48+ months. No surprises later. You can also model any scenario before you apply, using our public funding-cost calculator.",
        cta: { label: "Run the funding-cost calculator", to: "/tools/funding-cost-calculator" },
      },
    ],
  },

  // ─────────────────────────────────────────────────────────
  // Instabridge check — inline callout inside How-It-Works
  // ─────────────────────────────────────────────────────────
  instabridge_note:
    "The step other funders skip: after underwriting, we run a settlement-protection check. If the advance you asked for would exceed 15% of the conservative projected settlement, we offer you a smaller amount rather than approve you at a level that could put your net recovery at risk. It's a check other funding companies frequently don't run.",

  // ─────────────────────────────────────────────────────────
  // How it works — 5-step process, product-level not city-specific
  // ─────────────────────────────────────────────────────────
  how_it_works: {
    heading_template: "How pre-settlement funding works in {city}",
    intro:
      "The process is designed to be simple for the plaintiff and light on the attorney. Here is what actually happens from application to wire.",
    steps: [
      {
        n: 1,
        title: "You apply online",
        body:
          "A short form: your contact info, the state where your case is filed, your attorney's name and firm, and a brief description of the incident. About five minutes. No credit information, no personal financial disclosure. Nothing is submitted to any credit bureau at any point.",
      },
      {
        n: 2,
        title: "We contact your attorney",
        body:
          "Usually the same business day. We request the case file — the crash or incident report, current medical records and bills, any pre-suit demand letter or the filed complaint, and a short case memo. Most attorneys can send everything in 15 to 30 minutes.",
      },
      {
        n: 3,
        title: "Underwriting",
        body:
          "An underwriter — not a call-center rep, not an algorithm alone — reviews the case for liability strength, damages evidence, and expected timeline. On a straightforward car-accident case, this takes a few hours. On more complex cases (trucking, medical malpractice, product liability), 24–48 hours.",
      },
      {
        n: 4,
        title: "You review the offer",
        body:
          "If approved, you receive a written offer showing the advance amount, the full multi-year cost schedule (every 6-month tier from 6 months to 4+ years), and all Ohio-required disclosures. You have time to review, ask questions, and compare offers before signing.",
      },
      {
        n: 5,
        title: "Signing, attorney acknowledgment, and funding",
        body:
          "Signing is electronic. Your attorney signs a one-page lien acknowledgment stating they will honor the funding lien from settlement proceeds — Ohio law requires this before we can fund. Funds are wired the same or next business day; ACH is also available.",
      },
    ],
    when_it_fits:
      "Funding is a good fit when you have real bills or living expenses now, a case with clear liability and documented injuries, and a realistic multi-month or multi-year path to settlement. It is not a good fit if your case is expected to settle in the next 30–60 days (the fees accrue over time; waiting is cheaper) or if the advance would consume too large a share of the projected recovery. We tell applicants honestly when waiting makes more sense.",
    inline_cta: { to: "/how-it-works", label: "Read the full underwriting deep-dive" },
  },

  // ─────────────────────────────────────────────────────────
  // Statewide FAQs — funding + law questions that don't change by city
  // City-specific FAQs live in the city file and are prepended.
  // ─────────────────────────────────────────────────────────
  faqs: [
    {
      q: "Is pre-settlement funding legal in Ohio?",
      a: "Yes. Ohio Rev. Code § 1349.55 establishes the consumer-protection framework for pre-settlement funding contracts. The statute requires that funding be non-recourse, that specific disclosures be provided, that consumers have a five-day right of cancellation, and that the funding contract be acknowledged in writing by the consumer's attorney. Ohio HB 105, effective October 6, 2026, adds Chapter 1357 to the Ohio Revised Code, extending the cancellation window to ten business days and requiring funding companies to register with the Ohio Attorney General. Ohio's Supreme Court initially struck down these contracts as champerty and maintenance in Rancman v. Interim Settlement Funding Corp. (2003), and the Legislature then authorized them by enacting § 1349.55.",
    },
    {
      q: "What's the statute of limitations for an injury case in Ohio?",
      a: "Generally two years from the date of injury, under Ohio Rev. Code § 2305.10(A). Medical malpractice claims run under a shorter one-year statute (§ 2305.113(A)), extendable by 180 days with a written notice of intent to sue. Property-damage claims run under a four-year statute (§ 2305.09). Cases against a political subdivision (a city, county, or agency) have additional notice and timing requirements under Chapter 2744. Missing the applicable statute of limitations is fatal to the case, so this is one of the first things any Ohio personal-injury attorney will verify.",
    },
    {
      q: "Will Ohio's comparative-negligence rule hurt my recovery if I was partly at fault?",
      a: "Under Ohio Rev. Code § 2315.33, you can recover damages as long as your share of fault is 50% or less. Damages are reduced by your fault percentage — so if you're found 20% at fault and your damages are $100,000, you recover $80,000. If your fault percentage exceeds the combined fault of all defendants from whom you seek recovery, you recover nothing (the 51% bar). Comparative-fault exposure is one of the most important factors in evaluating cases where liability is contested; it also affects how we size an advance.",
    },
    {
      q: "How does the non-economic damages cap in Ohio affect my case?",
      a: "Ohio Rev. Code § 2315.18 caps non-economic damages (pain, suffering, mental anguish) at the greater of $250,000 or three times your economic damages, with an overall ceiling of $350,000 per plaintiff or $500,000 per single occurrence. Economic damages — past and future medical bills, lost income, out-of-pocket costs — are not capped. The cap does not apply if you've sustained permanent and substantial physical deformity, loss of use of a limb, loss of a bodily organ system, or a permanent physical functional injury preventing independent self-care. For a plaintiff with heavy documented medical bills, the cap has less effect than many people assume; for pain-and-suffering-heavy cases with limited economic damages, it can be significant. Juries are not informed of the cap.",
    },
    {
      q: "Do I need to tell my attorney about the funding?",
      a: "Yes. Ohio law under § 1349.55 (and § 1357.03 beginning October 6, 2026) requires your attorney to acknowledge the funding contract in writing before the funding company can advance the funds. Beyond the legal requirement, telling your attorney is practically essential: at settlement, your attorney satisfies the funding lien from the settlement proceeds through their trust account. They cannot do that if they don't know the lien exists. A reputable Ohio funding company will not fund without attorney acknowledgment.",
    },
    {
      q: "Can I get funding on a workplace injury case if I already have a BWC claim?",
      a: "Yes, if there is a third-party lawsuit or clear demand alongside your BWC claim. Ohio's BWC (Bureau of Workers' Compensation) is the exclusive workers' compensation carrier under R.C. § 4123.74, but it does not preclude a separate lawsuit against a non-employer third party (a subcontractor, equipment manufacturer, driver of another vehicle, or non-employer property owner). BWC's subrogation lien attaches to any third-party settlement — we account for the estimated lien when sizing your advance so your net recovery is protected.",
    },
  ],

  // ─────────────────────────────────────────────────────────
  // Snapshot copy — templated
  // ─────────────────────────────────────────────────────────
  snapshot_body_template:
    "Instabridge funds injured plaintiffs in {county} County (and across Ohio) at advance sizes from $500 up to $25,000, calibrated to a fraction of the conservative projected settlement rather than the optimistic one. Every advance is non-recourse: if your case loses or is dismissed, we absorb the loss. There is no credit check, no collections, and no impact on your credit report — either when you apply, while the advance is outstanding, or after your case resolves.",
  snapshot_what_it_is_not:
    "Pre-settlement funding is not a personal loan and not something you're personally on the hook for. Legally, it is a non-recourse purchase of an interest in your future recovery. It is currently regulated in Ohio under Ohio Revised Code § 1349.55; that section is expressly repealed effective October 6, 2026, and replaced by a new consumer-protection framework in R.C. Chapter 1357 (Ohio HB 105). The distinction matters: an Instabridge advance can never send you to collections, hurt your credit, or leave you owing money if the case doesn't recover.",

  // ─────────────────────────────────────────────────────────
  // Coverage note — same across cities
  // ─────────────────────────────────────────────────────────
  coverage_note:
    "Our funding process, pricing, and Ohio-required disclosures under R.C. § 1349.55 (and R.C. § 1357.03 beginning October 6, 2026) are the same regardless of which Ohio community you live in.",

  // Byline (organization-level; unchanged per city until Adam reviews individually)
  byline: {
    author_label: "Written by the Instabridge Funding editorial team",
    reviewer_label: "Reviewed by Adam Gurney, Founder & Attorney",
  },
};
